// =====================================================================
// useCotizadorManual.js
// Estado del Cotizador Manual. En vez de calcular un mensaje de WhatsApp
// (como antes), arma el payload con los datos capturados a mano y se lo
// manda al backend para que genere el mismo PDF con marca que usa el
// cotizador automático, seleccionando el template según el destino de la
// plantilla elegida (ver src/utils/destinosManualConfig.js "destino" y
// core/adaptarCotizacionManual.js en el backend).
// =====================================================================

import { ref, computed, watch } from "vue"
import Swal from "sweetalert2"
import * as calc from "@/utils/calculos"
import { dollar, rangoFechas, diffDias } from "@/utils/excelFormulas"
import {
  getDestinoManual,
  destinosManual,
  resolverDestinoLibre,
  zonasParaDestino
} from "@/utils/destinosManualConfig"
import { cargarParametros } from "@/utils/parametrosManual"
import { useDuracionInput } from "@/composables/useDuracionInput"
import api from "@/api/axios"
import { esperarJob } from "@/composables/useJobPolling"

const { validarYFormatearDuracion, bloquearDuracion } = useDuracionInput()

const nuevoHotel = () => ({ nombre: "", valor: 0 })

// Mismo shape que usa VueloManualModal.vue (cotizador automático) para que
// el asesor capture el vuelo exactamente igual en ambos formularios.
const nuevoTramoVuelo = () => ({
  fecha: "",
  aerolinea: "",
  salida: "",
  llegada: "",
  duracion: "",
  tipo: "directo",
  diasExtra: 0,
  escalas: []
})

export function useCotizadorManual() {
  // Trae de la BD los parámetros vigentes de las fórmulas (si cambian,
  // los computeds recalculan solos porque `parametros` es reactivo).
  cargarParametros(true)

  // ------------------------------- STATE -------------------------------
  const destinoManual = ref(destinosManual[0].value)
  const config = computed(() => getDestinoManual(destinoManual.value))

  const destinoNombre = ref("")
  const inicio = ref("")
  const fin = ref("")
  const pax = ref(2) // adultos (formulario básico / tour / crucero)
  const ninosManual = ref(0) // niños (formulario básico / tour / crucero)
  // Ya no es un campo del formulario: se trae sola, igual que en el
  // cotizador automático (destinos/*.js vía obtener_trm_actual(), utils.js),
  // y el asesor no la ve ni la edita. 4200 es solo el valor de arranque
  // mientras responde /trm-actual. Se le suma 100 al recibirla porque así
  // se usa en todo el sistema (TRM_modificada = TRM + 100 en utils.js /
  // pricing.js) — este ref queda con ese ajuste ya aplicado, tanto para
  // Turquía/Tailandia (calc.turquia/calc.tailandia, únicos cálculos que
  // hoy dependen de este valor en el frontend) como para el resto de
  // destinos, cuyo PDF final el backend recalcula de forma independiente
  // (ver core/adaptarCotizacionManual.js:comunes).
  const trm = ref(4200)
  api.get("/trm-actual")
    .then(({ data }) => {
      if (data?.trm) trm.value = data.trm + 100
    })
    .catch(err => {
      console.error("No se pudo obtener la TRM automática:", err)
    })
  const vuelos = ref(0)
  const traslados = ref(0)

  // Datos que necesita el PDF y que el mensaje de WhatsApp no requería
  const origen = ref("BOG")
  const telefono = ref("")
  const valorRecobro = ref(0)
  const vueloIda = ref(nuevoTramoVuelo())
  const vueloRegreso = ref(nuevoTramoVuelo())
  const vueloIntermedio = ref(nuevoTramoVuelo()) // solo mexCun
  const diasCombi1 = ref(0) // noches ciudad (mexCun/varaderoHabana/panamaCombinado)
  const diasCombi2 = ref(0) // noches playa/segunda ciudad

  // Hoteles por grupo (estandar / gruposTravel / otrosUsd)
  const hotelesPorGrupo = ref({})

  // Combinados (MEX-CUN, VAR-HAV, PTY)
  const hotelesGrupoA = ref([nuevoHotel()])
  const hotelesGrupoB = ref({})

  // Tour (Turquía) — sin PDF, solo mensaje (ver pdfNoDisponible)
  const inicioPlan = ref("")
  const vueloNeto = ref(0)
  const nochesAdicionales = ref(0)

  // Crucero — sin PDF, solo mensaje
  const fechaSalida = ref("")
  const cabinas = ref({
    interior: { sin: 0, con: 0 },
    exterior: { sin: 0, con: 0 },
    balcon: { sin: 0, con: 0 },
    suite: { sin: 0, con: 0 }
  })

  // San Andrés / Tailandia-BKK (ambos necesitan habitaciones por ocupación)
  const habitaciones = ref([{ adultos: 2, ninos: 0 }])
  const hotelesDesayuno = ref([nuevoHotel()]) // San Andrés, netos Travel Compositor
  const hotelesTodoIncluido = ref([nuevoHotel()]) // San Andrés, netos Travel Compositor

  // Tailandia (BKK)
  const hotelesBkk = ref({
    turista: nuevoHotel(),
    premium: nuevoHotel(),
    cincoEstrellas: nuevoHotel()
  })
  const diasBkk = ref(12) // 12 o 13

  // Costa (SMR / CTG) — una lista de hoteles por zona, todas simultáneas: el
  // template las muestra siempre juntas en el mismo PDF.
  const hotelesCostaPorZona = ref({})

  // Código de destino real que se va a mandar al backend: el de la
  // plantilla si tiene uno fijo, o el resuelto del texto libre si la
  // plantilla es genérica (destino "MANUAL" — TC Nacional/Internacional/
  // Grupos Travel/Otros USD). El formulario de hoteles sigue a ESTE valor,
  // no a la plantilla elegida — así "Cartagena"/"Santa Marta" escrito en
  // cualquier plantilla genérica muestra las zonas correctas sin tener que
  // cambiar de plantilla.
  const destinoEfectivo = computed(() => {
    const cfg = config.value
    if (!cfg) return null
    return cfg.destino === "MANUAL"
      ? (resolverDestinoLibre(destinoNombre.value) || "MANUAL")
      : cfg.destino
  })

  const zonasCostaEfectivas = computed(() => zonasParaDestino(destinoEfectivo.value))

  // Reconstruye hotelesCostaPorZona cuando el destino efectivo entra/sale
  // de CTG o SMR, o cambia entre ellos (zonas distintas).
  watch(destinoEfectivo, destino => {
    const zonas = zonasParaDestino(destino)
    if (!zonas) {
      hotelesCostaPorZona.value = {}
      return
    }
    const clavesActuales = Object.keys(hotelesCostaPorZona.value)
    const clavesNuevas = zonas.map(z => z.key)
    const mismasClaves = clavesActuales.length === clavesNuevas.length &&
      clavesNuevas.every(k => clavesActuales.includes(k))
    if (mismasClaves) return
    hotelesCostaPorZona.value = {}
    for (const z of zonas) hotelesCostaPorZona.value[z.key] = [nuevoHotel()]
  }, { immediate: true })

  // Resultado de la generación del PDF
  const generandoPdf = ref(false)
  const pdfUrl = ref(null)
  const errorPdf = ref("")

  // Reinicia estructuras al cambiar de destino
  watch(config, cfg => {
    hotelesPorGrupo.value = {}
    if (cfg?.grupos) {
      for (const g of cfg.grupos) {
        hotelesPorGrupo.value[g.key] = [nuevoHotel()]
      }
    }
    hotelesGrupoA.value = [nuevoHotel()]
    hotelesGrupoB.value = {}
    if (cfg?.grupoB) {
      for (const g of cfg.grupoB) {
        hotelesGrupoB.value[g.key] = [nuevoHotel()]
      }
    }
    if (cfg?.tipo === "sanAndres") {
      hotelesDesayuno.value = [nuevoHotel()]
      hotelesTodoIncluido.value = [nuevoHotel()]
    }
    // Si la nueva plantilla no tiene destino fijo, limpia el campo en vez de
    // dejar el texto de la plantilla anterior (ej. venir de "Punta Cana" y
    // pasar a "TC Nacional" no debe arrastrar "Punta Cana" en el campo).
    // hotelesCostaPorZona se sincroniza aparte, según destinoEfectivo (ver
    // watch(destinoEfectivo, ...) arriba) — no según esta plantilla.
    destinoNombre.value = cfg?.destinoFijo || ""

    if (cfg?.tipo === "sanAndres" || cfg?.tipo === "bkk") {
      habitaciones.value = [{ adultos: 2, ninos: 0 }]
    }
    if (cfg?.tipo === "bkk") {
      diasBkk.value = cfg.dias || 12
      hotelesBkk.value = {
        turista: nuevoHotel(),
        premium: nuevoHotel(),
        cincoEstrellas: nuevoHotel()
      }
    }

    vueloIda.value = nuevoTramoVuelo()
    vueloRegreso.value = nuevoTramoVuelo()
    vueloIntermedio.value = nuevoTramoVuelo()
    diasCombi1.value = 0
    diasCombi2.value = 0
    pdfUrl.value = null
    errorPdf.value = ""
  }, { immediate: true })

  // ------------- FECHAS (mismas reglas que el cotizador automático) -----
  // Ver src/composables/useFechas.js — misma lógica de min/max y de
  // auto-cálculo de la fecha de regreso, replicada acá porque el manual no
  // comparte esos refs con el formulario de búsqueda automática.

  const minFecha = new Date().toISOString().split("T")[0]

  const maxFecha = computed(() => {
    const f = new Date()
    f.setMonth(f.getMonth() + 10)
    f.setDate(f.getDate() + 15)
    return f
  })

  const minFechaRegreso = computed(() => {
    if (!inicio.value) return minFecha
    const f = new Date(inicio.value)
    f.setDate(f.getDate() + 1)
    return f.toISOString().split("T")[0]
  })

  // BKK: el circuito solo tiene salidas fijas Dom/Mar/Jue/Vie, igual que la
  // búsqueda automática (CreateModal.vue disabledDatesIda).
  const disabledDatesIda = date => {
    if (config.value?.tipo !== "bkk") return false
    return ![0, 2, 4, 5].includes(date.getDay())
  }

  // BKK/combinados calculan la fecha de regreso solos a partir de las
  // noches indicadas (dias/diasCombi1+2) y bloquean el campo, igual que
  // bloqueoRegreso en useFechas.js; el resto solo sugiere ida+1 día.
  const bloqueoRegreso = computed(() => {
    const d = config.value?.destino || ""
    return d.includes("COMBINADO") || config.value?.tipo === "bkk"
  })

  watch([inicio, () => config.value?.destino, diasCombi1, diasCombi2, diasBkk], () => {
    if (!inicio.value) return
    const cfg = config.value
    const f = new Date(inicio.value)

    if (cfg?.tipo === "bkk") {
      f.setDate(f.getDate() + (Number(diasBkk.value) || 12))
      fin.value = f.toISOString().split("T")[0]
      return
    }

    if ((cfg?.destino || "").includes("COMBINADO")) {
      f.setDate(f.getDate() + (Number(diasCombi1.value) || 0) + (Number(diasCombi2.value) || 0))
      fin.value = f.toISOString().split("T")[0]
      return
    }

    f.setDate(f.getDate() + 1)
    fin.value = f.toISOString().split("T")[0]
  })

  // ------------- VUELOS: escalas dinámicas (igual que VueloManualModal.vue) --

  function actualizarNumeroEscalas(vuelo, cantidad) {
    const total = Number(cantidad)
    if (!vuelo.escalas) vuelo.escalas = []
    while (vuelo.escalas.length < total) vuelo.escalas.push({ duracion: "", nombre: "" })
    while (vuelo.escalas.length > total) vuelo.escalas.pop()
  }

  function actualizarDuracion(vuelo, value) {
    vuelo.duracion = validarYFormatearDuracion(value)
  }

  watch(() => vueloIda.value.tipo, val => {
    if (val === "con escalas") {
      if (!vueloIda.value.escalas?.length) actualizarNumeroEscalas(vueloIda.value, 1)
    } else {
      vueloIda.value.escalas = []
    }
  })

  watch(() => vueloRegreso.value.tipo, val => {
    if (val === "con escalas") {
      if (!vueloRegreso.value.escalas?.length) actualizarNumeroEscalas(vueloRegreso.value, 1)
    } else {
      vueloRegreso.value.escalas = []
    }
  })

  watch(() => vueloIntermedio.value.tipo, val => {
    if (val === "con escalas") {
      if (!vueloIntermedio.value.escalas?.length) actualizarNumeroEscalas(vueloIntermedio.value, 1)
    } else {
      vueloIntermedio.value.escalas = []
    }
  })

  // --------------------------- HELPERS ---------------------------------

  const totalPaxSanAndres = computed(() =>
    habitaciones.value.reduce((a, h) => a + (h.adultos || 0) + (h.ninos || 0), 0)
  )

  const adultosSanAndres = computed(() =>
    habitaciones.value.reduce((a, h) => a + (h.adultos || 0), 0)
  )

  const ninosSanAndres = computed(() =>
    habitaciones.value.reduce((a, h) => a + (h.ninos || 0), 0)
  )

  function agregarHotel(lista) {
    lista.push(nuevoHotel())
  }

  function eliminarHotel(lista, index) {
    if (lista.length > 1) lista.splice(index, 1)
  }

  function agregarHabitacion() {
    if (habitaciones.value.length < 10) {
      habitaciones.value.push({ adultos: 2, ninos: 0 })
    }
  }

  function eliminarHabitacion(index) {
    if (habitaciones.value.length > 1) habitaciones.value.splice(index, 1)
  }

  const validacionesHabitaciones = computed(() =>
    habitaciones.value.map(h => calc.validarHabitacionSanAndres(h))
  )

  // ------------- MENSAJE WHATSAPP (solo Turquía / Crucero) --------------
  // Únicos dos casos sin PDF (cfg.pdfNoDisponible): son paquetes de precio
  // único (por persona o por cabina), no itemizados por hotel, y no calzan
  // con el modelo de precio de ningún template Nunjucks existente. Siguen
  // funcionando como antes, con el mensaje de WhatsApp para copiar.

  const resultadoTour = computed(() => {
    const cfg = config.value
    if (cfg?.tipo !== "tour" || !inicioPlan.value) return null
    const fn = calc[cfg.calculo]
    return fn({
      inicioPlan: inicioPlan.value,
      pax: Number(pax.value) + Number(ninosManual.value),
      trm: Number(trm.value),
      vueloNeto: Number(vueloNeto.value),
      nochesAdicionales: Number(nochesAdicionales.value)
    })
  })

  const resultadoCrucero = computed(() => {
    const cfg = config.value
    if (cfg?.tipo !== "crucero" || !fechaSalida.value) return null
    return calc.cruceroCtg({
      fechaSalida: fechaSalida.value,
      pax: Number(pax.value) + Number(ninosManual.value),
      cabinas: cabinas.value
    })
  })

  const mensajeSinPdf = computed(() => {
    const cfg = config.value
    if (!cfg?.pdfNoDisponible) return ""

    if (cfg.tipo === "tour") {
      const r = resultadoTour.value
      if (!r) return ""
      const totalPax = Number(pax.value) + Number(ninosManual.value)
      const lineas = [
        `*COTIZACION VIAJE PARA ${totalPax} PERSONAS A ${cfg.label.toUpperCase()}*`,
        "",
        cfg.saludo,
        "",
        rangoFechas(r.ida, r.regreso),
        "",
        `*VALOR POR PERSONA:* ${dollar(r.porPersona)}`
      ]
      if (cfg.calculo === "tailandia") {
        lineas.push(`*Niño acompañado de 2 adultos:* ${dollar(r.porPersonaNino)}`)
      }
      lineas.push("", cfg.incluye)
      return lineas.join("\n")
    }

    if (cfg.tipo === "crucero") {
      const r = resultadoCrucero.value
      if (!r) return ""
      const totalPax = Number(pax.value) + Number(ninosManual.value)
      return [
        `*COTIZACION PARA ${totalPax} PERSONAS EN CRUCERO DESDE CARTAGENA*`,
        rangoFechas(fechaSalida.value, r.regreso),
        "",
        "DIAS 📅 8 DIAS",
        "PUERTO DE SALIDA ⚓ CARTAGENA",
        `NOMBRE NAVIERA ➡ ${r.naviera}`,
        "",
        `*Cabina ${r.tipoCabina} Sin Bebidas*`,
        `🚢Interior USD ${r.precios.interior.sin}`,
        `🚢Exterior USD ${r.precios.exterior.sin}`,
        `🚢Balcon USD ${r.precios.balcon.sin}`,
        `🚢Suite USD ${r.precios.suite.sin}`,
        "",
        `*Cabina ${r.tipoCabina} Con Bebidas*`,
        `🚢Interior USD ${r.precios.interior.con}`,
        `🚢Exterior USD ${r.precios.exterior.con}`,
        `🚢Balcon USD ${r.precios.balcon.con}`,
        `🚢Suite USD ${r.precios.suite.con}`,
        "",
        "Los precios dados anteriormente son por el total de personas que viajan 💎💎💎 (Es el precio total que pagarias por todos en 1 cabina)"
      ].join("\n")
    }

    return ""
  })

  // ------------------------ GENERAR PDF ---------------------------------

  // Traduce el estado del formulario (que varía según cfg.tipo) al shape
  // plano que espera core/adaptarCotizacionManual.js en el backend.
  function armarHotelesPayload(cfg) {
    // Si el destino efectivo (código fijo de la plantilla, o resuelto del
    // texto libre en plantillas genéricas) es CTG o SMR, manda siempre
    // hotelesCostaPorZona — sin importar qué plantilla esté activa (ver
    // destinoEfectivo/zonasCostaEfectivas arriba).
    const zonas = zonasCostaEfectivas.value
    if (zonas) {
      const porZona = {}
      for (const z of zonas) {
        porZona[z.key] = (hotelesCostaPorZona.value[z.key] || []).map(h => ({
          nombre: h.nombre, valor: h.valor
        }))
      }
      return { hotelesCostaPorZona: porZona }
    }

    if (cfg.tipo === "estandar") {
      return { hoteles: hotelesPorGrupo.value }
    }

    if (cfg.tipo === "gruposTravel") {
      // El template genérico (Prueba.html) solo entiende "desayuno"/"ti";
      // "Desayuno y Cena" se pliega dentro de "desayuno".
      const desayuno = [
        ...(hotelesPorGrupo.value.desayuno || []),
        ...(hotelesPorGrupo.value.dc || [])
      ]
      return { hoteles: { desayuno, ti: hotelesPorGrupo.value.ti || [] } }
    }

    if (cfg.tipo === "otrosUsd") {
      return { hoteles: { desayuno: hotelesPorGrupo.value.hoteles || [] } }
    }

    if (cfg.tipo === "combinado") {
      const claveB = cfg.grupoB[0].key
      return {
        hoteles: {
          ciudad: hotelesGrupoA.value,
          playa: hotelesGrupoB.value[claveB] || []
        }
      }
    }

    if (cfg.tipo === "sanAndres") {
      return {
        hoteles: { desayuno: hotelesDesayuno.value, todo_incluido: hotelesTodoIncluido.value },
        habitaciones: habitaciones.value.map(h => ({
          adultos: h.adultos, ninos: h.ninos, infantes: 0
        }))
      }
    }

    if (cfg.tipo === "bkk") {
      return {
        hoteles: {
          turista: hotelesBkk.value.turista,
          premium: hotelesBkk.value.premium,
          cincoEstrellas: hotelesBkk.value.cincoEstrellas
        },
        habitaciones: habitaciones.value.map(h => ({
          adultos: h.adultos, ninos: h.ninos, infantes: 0
        })),
        dias: diasBkk.value
      }
    }

    return {}
  }

  // Igual que guardarVuelo() en useVueloManual.js: aplana el primer
  // elemento de "escalas" en escalaCiudad/duracionEscala antes de mandar el
  // tramo al backend.
  function aplanarTramo(vuelo, fecha) {
    return {
      ...vuelo,
      fecha,
      escalaCiudad: vuelo.escalas?.[0]?.nombre || "",
      duracionEscala: vuelo.escalas?.[0]?.duracion || ""
    }
  }

  // Mismo criterio que hotelesDesdeInputs() en core/adaptarCotizacionManual.js:
  // un hotel cuenta solo si tiene nombre y valor > 0 (los demás se descartan
  // y quedan como filas "-" / $0 en el PDF).
  const esHotelValido = h => Boolean(h?.nombre?.trim()) && Number(h?.valor) > 0

  // Basta con un hotel en CUALQUIER categoría (desayuno, todo incluido, zona,
  // ciudad/playa...): solo se bloquea si todas quedaron vacías.
  function hayAlgunHotel(extra) {
    const grupos = extra.hotelesCostaPorZona || extra.hoteles || {}
    return Object.values(grupos)
      .flatMap(g => (Array.isArray(g) ? g : [g]))
      .some(esHotelValido)
  }

  async function generarPdf() {
    const cfg = config.value
    if (!cfg || cfg.pdfNoDisponible) return

    if (!hayAlgunHotel(armarHotelesPayload(cfg))) {
      await Swal.fire({
        icon: "warning",
        title: "Falta al menos un hotel",
        text: "Agrega al menos un hotel (con nombre y valor) en alguna de las categorías, por ejemplo desayuno o todo incluido."
      })
      return
    }

    if (diffDias(inicio.value, fin.value) === 1) {
      const confirmacion = await Swal.fire({
        icon: "question",
        title: "¿Cotización de 1 sola noche?",
        text: "La fecha de regreso es un día después de la fecha de ida, por lo que la cotización sería de solo 1 noche. ¿Deseas continuar?",
        showCancelButton: true,
        confirmButtonText: "Sí, continuar",
        cancelButtonText: "Cancelar"
      })

      if (!confirmacion.isConfirmed) return
    }

    generandoPdf.value = true
    pdfUrl.value = null
    errorPdf.value = ""

    try {
      const extra = armarHotelesPayload(cfg)

      const adultos = cfg.tipo === "sanAndres" || cfg.tipo === "bkk"
        ? habitaciones.value.reduce((a, h) => a + (h.adultos || 0), 0)
        : Number(pax.value) || 0
      const ninos = cfg.tipo === "sanAndres" || cfg.tipo === "bkk"
        ? habitaciones.value.reduce((a, h) => a + (h.ninos || 0), 0)
        : Number(ninosManual.value) || 0

      // TC Internacional/Nacional/Grupos Travel/Otros USD mandan destino
      // "MANUAL" (plantilla genérica) y el destino real es el texto libre
      // que escribió el asesor. El FORMULARIO se queda igual (lista simple
      // hoteles.ti/hoteles.desayuno) — no se cambia de plantilla — pero si
      // ese texto coincide con un destino conocido (ej. "Brasil" → GIG,
      // "Santa Marta" → SMR, "Punta Cana" → PUJ), se manda ese código real
      // en vez de "MANUAL" para que el backend arme el PDF con el template,
      // recargo y nombre correctos (ver adaptarCotizacionManual.js: esos
      // adaptadores ya leen el mismo hoteles.ti/hoteles.desayuno).
      const destinoResuelto = cfg.destino === "MANUAL"
        ? (resolverDestinoLibre(destinoNombre.value) || cfg.destino)
        : cfg.destino

      const payload = {
        destino: destinoResuelto,
        destinoNombre: destinoNombre.value || "",
        origen: origen.value || "BOG",
        fechaIda: inicio.value,
        fechaRegreso: fin.value,
        adultos,
        ninos,
        infantes: 0,
        telefono: telefono.value,
        valorRecobro: Number(valorRecobro.value) || 0,
        precioVuelo: Number(vuelos.value) || 0,
        diasCombi1: Number(diasCombi1.value) || 0,
        diasCombi2: Number(diasCombi2.value) || 0,
        dias: cfg.tipo === "bkk" ? Number(diasBkk.value) || 12 : undefined,
        monedaHoteles: cfg.monedaHoteles || null,
        tramosVuelo: {
          ida: aplanarTramo(vueloIda.value, inicio.value),
          regreso: aplanarTramo(vueloRegreso.value, fin.value),
          intermedio: cfg.requiereTramoIntermedio
            ? aplanarTramo(vueloIntermedio.value, vueloIntermedio.value.fecha)
            : null
        },
        ...extra
      }

      const { data } = await api.post("/generar-cotizacion-manual", payload)
      if (!data.ok) throw new Error(data.error || "No se pudo iniciar la generación")

      const resultado = await esperarJob(data.jobId)
      const quoteCode = resultado?.quoteCode

      if (!quoteCode) {
        throw new Error("La cotización se generó pero no se recibió el código")
      }

      const { data: quoteData } = await api.get(`/quotes/by-code/${quoteCode}`)
      pdfUrl.value = quoteData.pdfUrl

    } catch (err) {
      errorPdf.value = err.response?.data?.error || err.message || "Error generando el PDF"
    } finally {
      generandoPdf.value = false
    }
  }

  return {
    // config
    destinosManual,
    destinoManual,
    config,

    // state
    destinoNombre,
    inicio,
    fin,
    pax,
    ninosManual,
    trm,
    vuelos,
    traslados,
    origen,
    telefono,
    valorRecobro,
    vueloIda,
    vueloRegreso,
    vueloIntermedio,
    diasCombi1,
    diasCombi2,
    hotelesPorGrupo,
    hotelesGrupoA,
    hotelesGrupoB,
    inicioPlan,
    vueloNeto,
    nochesAdicionales,
    fechaSalida,
    cabinas,
    habitaciones,
    hotelesDesayuno,
    hotelesTodoIncluido,
    hotelesBkk,
    diasBkk,
    hotelesCostaPorZona,
    zonasCostaEfectivas,

    // computeds
    totalPaxSanAndres,
    adultosSanAndres,
    ninosSanAndres,
    validacionesHabitaciones,
    resultadoTour,
    resultadoCrucero,
    mensajeSinPdf,

    // fechas (mismas reglas que el cotizador automático)
    minFecha,
    maxFecha,
    minFechaRegreso,
    bloqueoRegreso,
    disabledDatesIda,

    // generación de PDF
    generandoPdf,
    pdfUrl,
    errorPdf,
    generarPdf,

    // actions
    agregarHotel,
    eliminarHotel,
    agregarHabitacion,
    eliminarHabitacion,
    actualizarNumeroEscalas,
    actualizarDuracion,
    bloquearDuracion
  }
}
